import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../Shared/API/config';

// Shared sub-types
export interface ImpactItem {
  id?: string | number;
  label?: string;
  subjectId?: string | number;
}

// ARREARS_AT_RISK item
export interface ArrearsAtRiskItem {
  id: string;
  classId: string;
  name: string;
  count: number;
  amount: number;
  reason: string;
  impactItems?: ImpactItem[];
  dataContext?: { classroomId: string };
}

// FORGOTTEN_DEBTORS item
export interface ForgottenDebtorItem {
  id: string; // studentId
  name: string;
  amount: number;
  lastPaymentDate: string | null;
  daysSincePayment: number;
  reason: string;
  impactItems: ImpactItem[];
}

export interface ArrearsAtRiskData {
  isSummary: true;
  count: number;
  totalOutstanding: number;
  totalStudents: number;
  guidance: string;
  items: ArrearsAtRiskItem[];
}

export interface ForgottenDebtorsData {
  isSummary: true;
  count: number;
  guidance: string;
  items: ForgottenDebtorItem[];
}

export interface CollectionPaceBehindData {
  isSummary?: false;
  collectedPct: number;
  expectedPct: number;
  gap: number;
  outstanding: number;
  totalFees: number;
  guidance: string;
}

export interface MissingAttendanceData {
  classId: string;
  latestUnmarkedDate?: string;
}

export interface HighAbsenteeismData {
  studentId: string;
}

export type DiagnosticAlertData =
  | ArrearsAtRiskData
  | ForgottenDebtorsData
  | CollectionPaceBehindData
  | MissingAttendanceData
  | HighAbsenteeismData
  | Record<string, any>;

export interface DiagnosticAlert {
  id: number;
  ruleId: string;
  referenceId: string;
  title: string;
  message: string;
  severity: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  category: 'OPERATIONS' | 'ACADEMICS' | 'FINANCE' | 'SETUP' | 'SECURITY';
  data: DiagnosticAlertData;
  isRead: boolean;
  isDismissible: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DiagnosticAlertsResponse {
  data: DiagnosticAlert[];
}

export const DiagnosticAlertsApi = createApi({
  reducerPath: 'diagnosticAlertsApi',
  baseQuery,
  tagTypes: ['DiagnosticAlerts'],
  endpoints: builder => ({
    getDiagnosticAlerts: builder.query<DiagnosticAlertsResponse, { is_read?: boolean } | Record<string, never>>({
      query: (params) => ({
        url: '/diagnostics/alerts/',
        params,
      }),
      providesTags: ['DiagnosticAlerts'],
    }),
    markAlertAsRead: builder.mutation<DiagnosticAlert, number>({
      query: (alertId) => ({
        url: `/diagnostics/alerts/${alertId}/mark-read/`,
        method: 'POST',
      }),
      invalidatesTags: ['DiagnosticAlerts'],
    }),
    dismissAlert: builder.mutation<void, number>({
      query: (alertId) => ({
        url: `/diagnostics/alerts/${alertId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DiagnosticAlerts'],
    }),
    snoozeItem: builder.mutation<void, { ruleId: string; referenceId: string; days?: number }>({
      query: (body) => ({
        url: '/diagnostics/alerts/snooze/',
        method: 'POST',
        body: {
          rule_id: body.ruleId,
          reference_id: body.referenceId,
          days: body.days
        },
      }),
      invalidatesTags: ['DiagnosticAlerts'],
    }),
    snoozeBulkItems: builder.mutation<void, { ruleId: string; referenceIds: string[]; days?: number }>({
      query: (body) => ({
        url: '/diagnostics/alerts/snooze-bulk/',
        method: 'POST',
        body: {
          rule_id: body.ruleId,
          reference_ids: body.referenceIds,
          days: body.days,
        },
      }),
      invalidatesTags: ['DiagnosticAlerts'],
    }),
  }),
});

export const {
  useGetDiagnosticAlertsQuery,
  useMarkAlertAsReadMutation,
  useDismissAlertMutation,
  useSnoozeItemMutation,
  useSnoozeBulkItemsMutation,
} = DiagnosticAlertsApi;
